import { ContentItemType } from '../../../types/content/contentItems'
import { ContentType } from '../../../types/content/content'

export type LayoutMode = 'flat' | 'paged'
export type ResponseMode = 'none' | 'survey' | 'training'

export interface ContentSeedContext {
  pageId?: string
  externalUrl?: string
}

export interface ContentEditorItem {
  id: string
  type: ContentItemType
  index: number
  page?: string
  title?: string
  paragraph?: string
  short?: string
  url?: string
  label?: string
  checkbox_text?: string
  question?: string
  answers?: any[]
}

export interface ContentValidationContext {
  questions: ContentEditorItem[]
  minimumScore: number
  externalUrl?: string
}

export interface ContentEditorConfig {
  type: ContentType
  label: string
  group: 'learning' | 'survey' | 'content'
  description: string
  layoutMode: LayoutMode
  allowedItemTypes: ContentItemType[]
  requiresExternalUrl: boolean
  supportsMinimumScore: boolean
  supportsPageCrud: boolean
  autoSeedItems: (ctx: ContentSeedContext) => ContentEditorItem[]
  publishValidation: (ctx: ContentValidationContext) => string[]
  responseMode: ResponseMode
}

const GENERIC_ALLOWED = [
  ContentItemType.Paragraph,
  ContentItemType.Short,
  ContentItemType.Section,
  ContentItemType.Media,
]

const SURVEY_ALLOWED = [
  ContentItemType.Paragraph,
  ContentItemType.Section,
  ContentItemType.Media,
  ContentItemType.URL,
  ContentItemType.SurveyMCQ,
  ContentItemType.SurveyRating,
  ContentItemType.SurveyText,
]

const TRAINING_ALLOWED = [
  ContentItemType.Paragraph,
  ContentItemType.Section,
  ContentItemType.Media,
  ContentItemType.URL,
  ContentItemType.TrainingMCQ,
  ContentItemType.TrainingCheckbox,
]

const LEARNING_ALLOWED = [
  ContentItemType.Paragraph,
  ContentItemType.Section,
  ContentItemType.Media,
  ContentItemType.URL,
  ContentItemType.SurveyText,
  ContentItemType.LearningCheckbox,
]

const createId = () => `local-${Date.now()}-${Math.floor(Math.random() * 1000)}`

const defaultPublishValidation = ({ questions }: ContentValidationContext) => {
  const errors: string[] = []

  questions.forEach((question) => {
    switch (question.type) {
      case ContentItemType.Paragraph:
        if (!question.title?.trim() || !question.paragraph?.trim()) {
          errors.push('Paragraph items require a title and body.')
        }
        break
      case ContentItemType.Short:
        if (!question.title?.trim() || !question.short?.trim()) {
          errors.push('Short items require a title and text.')
        }
        break
      case ContentItemType.Section:
        if (!question.title?.trim()) {
          errors.push('Section items require a title.')
        }
        break
      case ContentItemType.Media:
        if (!question.url && !question.title && !(question as any).media) {
          errors.push('Media items require uploaded media.')
        }
        break
      case ContentItemType.URL:
        if (!question.url?.trim()) {
          errors.push('URL items require a link.')
        }
        break
      case ContentItemType.SurveyMCQ:
        if (!question.question?.trim() || !Array.isArray(question.answers) || question.answers.length < 2) {
          errors.push('Survey multiple choice items require a question and at least two answers.')
        }
        break
      case ContentItemType.SurveyRating:
      case ContentItemType.SurveyText:
        if (!question.question?.trim()) {
          errors.push('Survey items require a question.')
        }
        break
      case ContentItemType.TrainingMCQ:
      case ContentItemType.TrainingCheckbox:
        if (!question.question?.trim()) {
          errors.push('Training questions require prompt text.')
        }
        if (!Array.isArray(question.answers) || question.answers.length === 0) {
          errors.push('Training questions require answer options.')
        }
        if (!question.answers?.some((answer: any) => answer.correct)) {
          errors.push('Training questions require at least one correct answer.')
        }
        break
      case ContentItemType.LearningCheckbox:
        if (!question.label?.trim() && !question.title?.trim()) {
          errors.push('Completion checkbox items require a label.')
        }
        break
      default:
        break
    }
  })

  return errors
}

const learningSeed = ({ pageId, externalUrl }: ContentSeedContext): ContentEditorItem[] => {
  const scoped = (item: Omit<ContentEditorItem, 'id' | 'index'>, index: number): ContentEditorItem => ({
    id: createId(),
    index,
    ...(pageId ? { page: pageId } : {}),
    ...item,
  })

  return [
    scoped(
      {
        type: ContentItemType.URL,
        url: externalUrl || '',
      },
      0,
    ),
    scoped(
      {
        type: ContentItemType.Section,
        title: 'Upon completion of the training please tick this box and provide some feedback on the training',
      },
      1,
    ),
    scoped(
      {
        type: ContentItemType.LearningCheckbox,
        title: 'I have completed this training',
        label: 'I have completed this training',
        checkbox_text: 'Confirm',
      },
      2,
    ),
    scoped(
      {
        type: ContentItemType.Section,
        title: 'Feedback',
      },
      3,
    ),
    scoped(
      {
        type: ContentItemType.SurveyText,
        question: 'Please provide your feedback on the training',
      },
      4,
    ),
  ]
}

export const CONTENT_EDITOR_CONFIG: Record<ContentType, ContentEditorConfig> = {
  [ContentType.Training]: {
    type: ContentType.Training,
    label: 'Training',
    group: 'learning',
    description: 'Build your own e-learning flow with pages, rich content, and quiz scoring.',
    layoutMode: 'paged',
    allowedItemTypes: TRAINING_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: true,
    supportsPageCrud: true,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'training',
  },
  [ContentType.Learning]: {
    type: ContentType.Learning,
    label: 'Learning',
    group: 'learning',
    description: 'Link to an external e-learning URL and collect completion plus feedback.',
    layoutMode: 'flat',
    allowedItemTypes: LEARNING_ALLOWED,
    requiresExternalUrl: true,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: learningSeed,
    publishValidation: ({ questions, externalUrl }) => {
      const errors = defaultPublishValidation({ questions, minimumScore: 0, externalUrl })
      if (!externalUrl?.trim()) errors.push('Learning content requires an external URL.')
      return errors
    },
    responseMode: 'survey',
  },
  [ContentType.Survey]: {
    type: ContentType.Survey,
    label: 'Survey',
    group: 'survey',
    description: 'Collect feedback with survey blocks and no quiz scoring.',
    layoutMode: 'flat',
    allowedItemTypes: SURVEY_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'survey',
  },
  [ContentType.AnonymousSurvey]: {
    type: ContentType.AnonymousSurvey,
    label: 'Anonymous survey',
    group: 'survey',
    description: 'Collect feedback without storing who submitted each response.',
    layoutMode: 'flat',
    allowedItemTypes: SURVEY_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'survey',
  },
  [ContentType.Skills]: {
    type: ContentType.Skills,
    label: 'Skills',
    group: 'content',
    description: 'General learning content for skill development.',
    layoutMode: 'flat',
    allowedItemTypes: GENERIC_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'none',
  },
  [ContentType.Competence]: {
    type: ContentType.Competence,
    label: 'Competence',
    group: 'content',
    description: 'Reusable competence content with standard learning blocks.',
    layoutMode: 'flat',
    allowedItemTypes: GENERIC_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'none',
  },
  [ContentType.Task]: {
    type: ContentType.Task,
    label: 'Task',
    group: 'content',
    description: 'Task content with simple blocks and no response logic.',
    layoutMode: 'flat',
    allowedItemTypes: GENERIC_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'none',
  },
  [ContentType.Goal]: {
    type: ContentType.Goal,
    label: 'Goal',
    group: 'content',
    description: 'Goal-oriented content with standard content blocks.',
    layoutMode: 'flat',
    allowedItemTypes: GENERIC_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'none',
  },
  [ContentType.Note]: {
    type: ContentType.Note,
    label: 'Note',
    group: 'content',
    description: 'Short-form content composed of standard learning blocks.',
    layoutMode: 'flat',
    allowedItemTypes: GENERIC_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'none',
  },
  [ContentType.Form]: {
    type: ContentType.Form,
    label: 'Form',
    group: 'content',
    description: 'Structured content using reusable content blocks.',
    layoutMode: 'flat',
    allowedItemTypes: GENERIC_ALLOWED,
    requiresExternalUrl: false,
    supportsMinimumScore: false,
    supportsPageCrud: false,
    autoSeedItems: () => [],
    publishValidation: defaultPublishValidation,
    responseMode: 'none',
  },
}

export const getContentEditorConfig = (type: ContentType) =>
  CONTENT_EDITOR_CONFIG[type] ?? CONTENT_EDITOR_CONFIG[ContentType.Skills]

export const getContentPickerGroups = () => {
  const options = Object.values(CONTENT_EDITOR_CONFIG)
  return {
    learning: options.filter((option) => option.group === 'learning'),
    survey: options.filter((option) => option.group === 'survey'),
    content: options.filter((option) => option.group === 'content'),
  }
}
